import { useFormContext } from "react-hook-form";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormValues } from "../schema";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useNetwork } from "wagmi";

const MIN_PRICE_PER_TOKEN = 0.00000001; // Minimum allowed price in BNB

const calculateSuggestedTokenAmounts = (bnbAmount: number, totalSupply: number): number[] => {
  // Calculate some reasonable token amounts based on BNB provided
  const lowPrice = bnbAmount * 100000;     // 0.000001 BNB per token
  const medPrice = bnbAmount * 10000;      // 0.00001 BNB per token
  const highPrice = bnbAmount * 1000;      // 0.0001 BNB per token
  
  // Return amounts that don't exceed total supply
  return [lowPrice, medPrice, highPrice]
    .filter(amount => amount <= totalSupply)
    .sort((a, b) => a - b);
};

const formatTokenAmount = (amount: number): string => {
  if (amount >= 1000000) {
    return `${(amount / 1000000).toFixed(2)}M tokens`;
  } else if (amount >= 1000) {
    return `${(amount / 1000).toFixed(2)}K tokens`;
  }
  return `${amount.toFixed(2)} tokens`;
};

const getTokenPriceDescription = (bnbAmount: number, tokenAmount: number, currency: string): string => {
  if (tokenAmount <= 0) return "";
  const pricePerToken = bnbAmount / tokenAmount;
  return `Price per token: ${pricePerToken.toFixed(8)} ${currency}`;
};

const getMaxAllowedTokens = (bnbAmount: number): number => {
  return Math.floor(bnbAmount / MIN_PRICE_PER_TOKEN);
};

export function InitialLiquidity() {
  const form = useFormContext<FormValues>();
  const { chain } = useNetwork();
  const isTestnet = chain?.id === 97 || chain?.id === 11155111 || chain?.id === 43113 || chain?.id === 5611; // BSC Testnet, Sepolia, Avalanche Fuji, or opBNB Testnet
  const isBsc = chain?.id === 56 || chain?.id === 97 || chain?.id === 204 || chain?.id === 5611; // BSC & opBNB networks
  const isAvaxMainnet = chain?.id === 43114; // Avalanche mainnet

  // Get native currency based on chain
  const getNativeCurrency = () => {
    if (chain?.id === 56 || chain?.id === 97 || chain?.id === 204 || chain?.id === 5611) return 'BNB'; // BSC & opBNB
    if (chain?.id === 1 || chain?.id === 11155111) return 'ETH';
    if (chain?.id === 43114 || chain?.id === 43113) return 'AVAX';
    if (chain?.id === 11124 || chain?.id === 2741) return 'ETH'; // Abstract Testnet & Mainnet
    if (chain?.id === 8453 || chain?.id === 84532) return 'ETH'; // Base & Base Sepolia
    if (chain?.id === 10 || chain?.id === 11155420) return 'ETH'; // OP Mainnet & Sepolia
    if (chain?.id === 42161 || chain?.id === 421614) return 'ETH'; // Arbitrum One & Sepolia
    return 'ETH'; // Default to ETH
  };

  // Get minimum liquidity requirement based on network
  const getMinimumLiquidity = () => {
    if (isTestnet) return 0;
    if (isAvaxMainnet) return 100;
    if (chain?.id === 1) return 1; // ETH mainnet
    return 2.5; // BSC mainnet default
  };

  const currency = getNativeCurrency();
  const minimumLiquidity = getMinimumLiquidity();

  // Watch form values with useEffect to prevent excessive re-renders
  const [formValues, setFormValues] = useState({
    bnbAmount: "0",
    totalSupply: "0",
    customBnbAmount: "0",
    selectedTokenAmount: "",
    customTokenAmount: "",
    tradingEnabled: false,
    permanentTrading: false
  });

  // Use a single subscription to watch form changes
  useEffect(() => {
    const subscription = form.watch((value) => {
      if (value) {
        setFormValues({
          bnbAmount: value.initialLiquidity?.bnbAmount || "0",
          totalSupply: value.totalSupply || "0",
          customBnbAmount: value.initialLiquidity?.customAmount || "0",
          selectedTokenAmount: value.initialLiquidity?.tokenAmount || "",
          customTokenAmount: value.initialLiquidity?.customTokenAmount || "",
          tradingEnabled: value.tradingEnabled || false,
          permanentTrading: value.permanentTrading || false
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const {
    bnbAmount,
    totalSupply,
    customBnbAmount,
    selectedTokenAmount,
    customTokenAmount,
    tradingEnabled,
    permanentTrading
  } = formValues;

  const showLockOptions = bnbAmount && bnbAmount !== "0";
  const actualBnbAmount = bnbAmount === "custom" ? parseFloat(customBnbAmount) : parseFloat(bnbAmount || "0");
  const suggestedAmounts = useMemo(() => calculateSuggestedTokenAmounts(actualBnbAmount, parseFloat(totalSupply)), [actualBnbAmount, totalSupply]);
  const formattedTotalSupply = useMemo(() => parseFloat(totalSupply).toLocaleString(), [totalSupply]);
  const isLiquidityEnabled = tradingEnabled || permanentTrading;

  // Reset lock duration when amount changes to 0
  useEffect(() => {
    if (!showLockOptions) {
      form.setValue("initialLiquidity.lockDuration", "0", { shouldValidate: false });
      form.setValue("initialLiquidity.customLockDuration", "", { shouldValidate: false });
    }
  }, [showLockOptions, form]);

  // Validate custom amount based on network
  useEffect(() => {
    if (bnbAmount === "custom" && !isTestnet && parseFloat(customBnbAmount) < minimumLiquidity) {
      toast.warning(`Minimum ${minimumLiquidity} ${currency} required for initial liquidity on mainnet`);
    }
  }, [customBnbAmount, bnbAmount, isTestnet, currency, minimumLiquidity]);

  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="rounded-lg border bg-card text-card-foreground">
      <div className="flex items-center justify-between p-6">
        <div>
          <h3 className="text-lg font-semibold">Initial Liquidity</h3>
          <p className="text-sm text-muted-foreground">Configure your token's initial liquidity</p>
        </div>
        <span
          className={`transform transition-transform duration-200 cursor-pointer ${isOpen ? 'rotate-180' : ''}`}
          style={{
            color: `var(--color-secondary, #10b981)`
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          ▼
        </span>
      </div>
      <div className={`transition-all duration-200 overflow-hidden ${isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="p-6 pt-0">
          <div className="rounded-lg border bg-card text-card-foreground">
            <div className="p-6">
              <h3 className="text-lg font-semibold">Initial Liquidity</h3>
              {!isLiquidityEnabled && (
                <Alert variant="destructive" className="bg-orange-500/10 text-orange-400 border-orange-500/20">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>
                    Trading must be enabled to add initial liquidity. Please enable either "Trading Enabled" or "Permanent Trading" in the Security Features section.
                  </AlertDescription>
                </Alert>
              )}

              {!isTestnet && (
                <Alert className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>
                    Minimum {minimumLiquidity} {currency} required for initial liquidity on mainnet
                  </AlertDescription>
                </Alert>
              )}

              {/* Total Supply Display */}
              <div className="text-sm text-muted-foreground mb-2">
                Total Supply: {formattedTotalSupply} tokens
              </div>

              {/* Amount Selection */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="initialLiquidity.bnbAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Initial Liquidity ({currency})</FormLabel>
                      <Select
                        value={field.value || "0"} // Ensure "0" is used if value is empty
                        onValueChange={(value) => {
                          field.onChange(value);
                          if (value === "custom") {
                            form.setValue("initialLiquidity.customAmount", "");
                          }
                          form.setValue("initialLiquidity.tokenAmount", "");
                          form.setValue("initialLiquidity.customTokenAmount", "");
                        }}
                        disabled={!isLiquidityEnabled}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue defaultValue="0" placeholder="Select amount" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="0">No initial liquidity</SelectItem>
                          {isAvaxMainnet ? (
                            // Avalanche mainnet options (min 100 AVAX)
                            <>
                              <SelectItem value="100">100 AVAX</SelectItem>
                              <SelectItem value="200">200 AVAX</SelectItem>
                              <SelectItem value="300">300 AVAX</SelectItem>
                              <SelectItem value="400">400 AVAX</SelectItem>
                              <SelectItem value="500">500 AVAX</SelectItem>
                              <SelectItem value="600">600 AVAX</SelectItem>
                              <SelectItem value="700">700 AVAX</SelectItem>
                              <SelectItem value="800">800 AVAX</SelectItem>
                              <SelectItem value="900">900 AVAX</SelectItem>
                              <SelectItem value="1000">
                                <div className="flex items-center gap-2">
                                  1000 AVAX 🐋 (Whale-Proof)
                                </div>
                              </SelectItem>
                            </>
                          ) : isTestnet ? (
                            // Testnet options (no minimum)
                            <>
                              <SelectItem value="0.1">0.1 {currency}</SelectItem>
                              <SelectItem value="0.5">0.5 {currency}</SelectItem>
                              <SelectItem value="1">1 {currency}</SelectItem>
                              <SelectItem value="2">2 {currency}</SelectItem>
                              <SelectItem value="2.5">2.5 {currency}</SelectItem>
                              <SelectItem value="5">5 {currency}</SelectItem>
                              <SelectItem value="7.5">7.5 {currency}</SelectItem>
                              <SelectItem value="10">10 {currency}</SelectItem>
                              <SelectItem value="25">25 {currency}</SelectItem>
                              <SelectItem value="50">50 {currency}</SelectItem>
                              <SelectItem value="100">
                                <div className="flex items-center gap-2">
                                  100 {currency} 🐋 (Whale-Proof)
                                </div>
                              </SelectItem>
                            </>
                          ) : chain?.id === 1 ? (
                            // ETH mainnet options (min 1 ETH)
                            <>
                              <SelectItem value="1">1 ETH</SelectItem>
                              <SelectItem value="2">2 ETH</SelectItem>
                              <SelectItem value="5">5 ETH</SelectItem>
                              <SelectItem value="10">10 ETH</SelectItem>
                              <SelectItem value="25">25 ETH</SelectItem>
                              <SelectItem value="50">50 ETH</SelectItem>
                              <SelectItem value="100">
                                <div className="flex items-center gap-2">
                                  100 ETH 🐋 (Whale-Proof)
                                </div>
                              </SelectItem>
                            </>
                          ) : (
                            // BSC mainnet options (min 2.5 BNB)
                            <>
                              <SelectItem value="2.5">2.5 BNB</SelectItem>
                              <SelectItem value="5">5 BNB</SelectItem>
                              <SelectItem value="7.5">7.5 BNB</SelectItem>
                              <SelectItem value="10">10 BNB</SelectItem>
                              <SelectItem value="25">25 BNB</SelectItem>
                              <SelectItem value="50">50 BNB</SelectItem>
                              <SelectItem value="100">
                                <div className="flex items-center gap-2">
                                  100 BNB 🐋 (Whale-Proof)
                                </div>
                              </SelectItem>
                            </>
                          )}
                          <SelectItem value="custom">Custom amount</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {bnbAmount === "custom" && (
                  <FormField
                    control={form.control}
                    name="initialLiquidity.customAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Custom Amount ({currency})</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min={isTestnet ? "0" : minimumLiquidity.toString()}
                            placeholder="Enter custom amount"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e);
                              const value = parseFloat(e.target.value);
                              if (!isTestnet && value < minimumLiquidity) {
                                toast.warning(`Minimum ${minimumLiquidity} ${currency} required for initial liquidity on mainnet`);
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Token Amount */}
                {actualBnbAmount > 0 && (
                  <div className="space-y-4">
                    <FormField
                      control={form.control}
                      name="initialLiquidity.tokenAmount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Token Amount for Liquidity</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              min="0"
                              max={totalSupply}
                              step="1"
                              placeholder={`Enter amount (max: ${totalSupply.toLocaleString()} tokens)`}
                              value={field.value || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                const tokenAmount = parseFloat(value);
                                const maxAllowed = getMaxAllowedTokens(actualBnbAmount);
                                
                                if (tokenAmount > maxAllowed) {
                                  const requiredBNB = (tokenAmount * MIN_PRICE_PER_TOKEN).toFixed(2);
                                  toast.error(`This amount would make the price too low. Either:\n- Reduce tokens to ${maxAllowed.toLocaleString()} or less\n- Increase ${currency} to ${requiredBNB} ${currency}`);
                                  return;
                                }
                                
                                field.onChange(value);
                                
                                if (value && actualBnbAmount > 0 && totalSupply > 0) {
                                  const pricePerToken = actualBnbAmount / tokenAmount;
                                  
                                  if (pricePerToken > 1) {
                                    toast.warning("Warning: Token price is very high");
                                  }
                                }
                              }}
                            />
                          </FormControl>
                          {field.value && (
                            <div className="text-sm text-muted-foreground mt-1">
                              {getTokenPriceDescription(actualBnbAmount, parseFloat(field.value), currency)}
                            </div>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Token Amount Slider */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>0 (0%)</span>
                        <span>{totalSupply.toLocaleString()} (100%)</span>
                      </div>
                      <FormField
                        control={form.control}
                        name="initialLiquidity.tokenAmount"
                        render={({ field }) => (
                          <FormItem>
                            <Slider
                              min={0}
                              max={Math.min(totalSupply, getMaxAllowedTokens(actualBnbAmount))}
                              step={Math.max(1, totalSupply / 1000)} // Ensure minimum step is 1
                              value={[parseFloat(field.value) || 0]}
                              onValueChange={(values) => {
                                const value = Math.round(values[0]).toString(); // Round to whole numbers
                                field.onChange(value);
                                
                                if (actualBnbAmount > 0 && totalSupply > 0) {
                                  const tokenAmount = parseFloat(value);
                                  const pricePerToken = actualBnbAmount / tokenAmount;
                                  
                                  if (pricePerToken > 1) {
                                    toast.warning("Warning: Token price is very high");
                                  }
                                }
                              }}
                            />
                            {field.value && (
                              <div className="text-sm text-muted-foreground mt-1">
                                Selected: {parseInt(field.value).toLocaleString()} tokens ({((parseFloat(field.value) / totalSupply) * 100).toFixed(2)}%)
                              </div>
                            )}
                            {actualBnbAmount > 0 && (
                              <div className="text-sm text-blue-400 mt-1">
                                Maximum allowed: {getMaxAllowedTokens(actualBnbAmount).toLocaleString()} tokens to maintain minimum price of {MIN_PRICE_PER_TOKEN} {currency}
                              </div>
                            )}
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                )}

                {/* Custom Token Amount */}
                {selectedTokenAmount === "custom" && actualBnbAmount > 0 && (
                  <FormField
                    control={form.control}
                    name="initialLiquidity.customTokenAmount"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Custom Token Amount</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="1000"
                            min="1000"
                            max={totalSupply}
                            placeholder={`Enter amount (max: ${formattedTotalSupply} tokens)`}
                            {...field}
                          />
                        </FormControl>
                        {field.value && actualBnbAmount && (
                          <div className="space-y-1 mt-1">
                            <p className="text-sm text-gray-500">
                              {getTokenPriceDescription(actualBnbAmount, parseFloat(field.value), currency)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {parseFloat(field.value).toLocaleString()} of {totalSupply.toLocaleString()} total tokens ({((parseFloat(field.value) / totalSupply) * 100).toFixed(2)}%)
                            </p>
                          </div>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function InitialLiquidityInput() {
  const form = useFormContext<FormValues>();

  return (
    <FormField
      control={form.control}
      name="initialLiquidityInBNB"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Initial Liquidity (BNB)</FormLabel>
          <FormControl>
            <Input
              type="number"
              step="0.01"
              min="0"
              placeholder="Enter amount in BNB"
              {...field}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
